import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Shield, Activity, LogIn, LogOut, AlertTriangle, FilePlus, FileEdit, Trash2, Clock, MapPin } from "lucide-react";

function fmtTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtHm(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

const EVENT_ICONS = {
  login_success: { Icon: LogIn, cls: "text-emerald-600", label: "Login" },
  login_failure: { Icon: AlertTriangle, cls: "text-rose-600", label: "Failed login" },
  login_blocked: { Icon: Shield, cls: "text-amber-600", label: "Blocked" },
  logout: { Icon: LogOut, cls: "text-slate-500", label: "Logout" },
};

const ACTION_ICONS = {
  create: { Icon: FilePlus, cls: "text-emerald-600", label: "Created" },
  update: { Icon: FileEdit, cls: "text-indigo-600", label: "Updated" },
  delete: { Icon: Trash2, cls: "text-rose-600", label: "Deleted" },
};

export default function UserActivityLogDialog({ open, employee, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !employee?.id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/employees/${employee.id}/activity-log?days=60&limit=200`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, employee?.id]);

  const auth = data?.auth_events || [];
  const chg = data?.change_events || [];
  const act = data?.daily_activity || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[90vh] max-h-[90vh] p-0 flex flex-col overflow-hidden"
        data-testid="activity-log-dialog"
      >
        <DialogHeader className="p-4 pb-2 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <DialogTitle className="heading flex items-center gap-2">
            <Activity size={18} className="text-indigo-600" />
            Activity Log — {employee?.name || "Employee"}
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Last 60 days · auth events · all changes · daily active/idle time.
            Older entries auto-delete (60-day retention).
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-hidden px-4 pb-4">

        {loading ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
        ) : (
          <Tabs defaultValue="auth" className="w-full h-full flex flex-col mt-3">
            <TabsList className="grid w-full grid-cols-3 shrink-0">
              <TabsTrigger value="auth" data-testid="activity-tab-auth">Auth ({auth.length})</TabsTrigger>
              <TabsTrigger value="changes" data-testid="activity-tab-changes">Changes ({chg.length})</TabsTrigger>
              <TabsTrigger value="daily" data-testid="activity-tab-daily">Daily Activity ({act.length})</TabsTrigger>
            </TabsList>

            {/* ---- AUTH TAB ---- */}
            <TabsContent value="auth" className="mt-3 flex-1 min-h-0 overflow-hidden">
              <ScrollArea className="h-full pr-2">
                {auth.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">No auth events in this window.</div>
                ) : (
                  <div className="space-y-1.5">
                    {auth.map((r) => {
                      const meta = EVENT_ICONS[r.event] || EVENT_ICONS.login_success;
                      const { Icon } = meta;
                      return (
                        <div key={r.id} className="flex items-start gap-2.5 p-2.5 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid="activity-auth-row">
                          <Icon size={14} className={`shrink-0 mt-0.5 ${meta.cls}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-1.5 flex-wrap">
                              {meta.label}
                              {r.reason && r.reason !== "ok" && (
                                <Badge variant="outline" className="text-[9px] uppercase">{r.reason.replace(/_/g, " ")}</Badge>
                              )}
                              {!r.success && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[9px] uppercase">Denied</Badge>}
                            </div>
                            <div className="text-[10.5px] text-slate-500 mt-0.5 mono flex flex-wrap items-center gap-x-3 gap-y-0.5">
                              <span><Clock size={9} className="inline mr-1" />{fmtTs(r.created_at)}</span>
                              {r.ip && <span><MapPin size={9} className="inline mr-1" />{r.ip}</span>}
                              {r.email && r.event === "login_failure" && <span>email: {r.email}</span>}
                            </div>
                            {r.user_agent && (
                              <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={r.user_agent}>
                                {r.user_agent}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* ---- CHANGES TAB ---- */}
            <TabsContent value="changes" className="mt-3 flex-1 min-h-0 overflow-hidden">
              <ScrollArea className="h-full pr-2">
                {chg.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">No changes in this window.</div>
                ) : (
                  <div className="space-y-1.5">
                    {chg.map((r) => {
                      const meta = ACTION_ICONS[r.action] || ACTION_ICONS.update;
                      const { Icon } = meta;
                      const diffEntries = Object.entries(r.diff || {}).slice(0, 5);
                      return (
                        <div key={r.id} className="p-2.5 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid="activity-change-row">
                          <div className="flex items-start gap-2.5">
                            <Icon size={14} className={`shrink-0 mt-0.5 ${meta.cls}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-1.5 flex-wrap">
                                {meta.label}
                                <Badge variant="outline" className="text-[9px] uppercase">{r.module_key}</Badge>
                                {r.summary && <span className="text-slate-600 dark:text-slate-300 font-normal truncate">· {r.summary}</span>}
                              </div>
                              <div className="text-[10.5px] text-slate-500 mt-0.5 mono">
                                <Clock size={9} className="inline mr-1" />{fmtTs(r.created_at)} · record {r.record_id?.slice(-8) || "—"}
                              </div>
                              {diffEntries.length > 0 && (
                                <div className="mt-1 text-[10.5px] text-slate-600 dark:text-slate-300 space-y-0.5">
                                  {diffEntries.map(([k, v]) => (
                                    <div key={k} className="truncate">
                                      <span className="font-semibold">{k}:</span>{" "}
                                      <span className="text-rose-600 line-through">{String(v?.from ?? "—").slice(0, 60)}</span>{" → "}
                                      <span className="text-emerald-700">{String(v?.to ?? "—").slice(0, 60)}</span>
                                    </div>
                                  ))}
                                  {Object.keys(r.diff || {}).length > 5 && (
                                    <div className="text-slate-400 italic">+{Object.keys(r.diff).length - 5} more fields…</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* ---- DAILY ACTIVITY TAB ---- */}
            <TabsContent value="daily" className="mt-3 flex-1 min-h-0 overflow-hidden">
              <ScrollArea className="h-full pr-2">
                {act.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">No activity recorded in this window.</div>
                ) : (
                  <div className="space-y-1.5">
                    {act.map((r) => {
                      const totalSec = (Number(r.active_seconds) || 0) + (Number(r.idle_seconds) || 0);
                      const pct = totalSec > 0 ? Math.round((100 * (r.active_seconds || 0)) / totalSec) : 0;
                      return (
                        <div key={r.id} className="p-2.5 rounded-md border border-slate-200 dark:border-slate-700" data-testid="activity-daily-row">
                          <div className="flex items-start gap-3">
                            <div className="shrink-0 w-20 text-center">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500">Date</div>
                              <div className="text-[13px] font-bold text-slate-800 dark:text-slate-100 mono">{r.day}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="grid grid-cols-3 gap-2 text-[11px]">
                                <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded p-1.5">
                                  <div className="text-[9px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Active</div>
                                  <div className="font-bold text-emerald-800 dark:text-emerald-200 mono">{fmtHm(r.active_seconds)}</div>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded p-1.5">
                                  <div className="text-[9px] uppercase tracking-wider text-slate-600 dark:text-slate-400">Idle</div>
                                  <div className="font-bold text-slate-700 dark:text-slate-200 mono">{fmtHm(r.idle_seconds)}</div>
                                </div>
                                <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/30 rounded p-1.5">
                                  <div className="text-[9px] uppercase tracking-wider text-indigo-700 dark:text-indigo-300">Focus</div>
                                  <div className="font-bold text-indigo-800 dark:text-indigo-200 mono">{pct}%</div>
                                </div>
                              </div>
                              <div className="text-[10.5px] text-slate-500 mt-1.5 mono flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                <span>First seen: {fmtTs(r.first_seen_at)}</span>
                                <span>Last seen: {fmtTs(r.last_seen_at)}</span>
                                {r.last_ip && <span><MapPin size={9} className="inline mr-1" />{r.last_ip}</span>}
                                {r.last_page && <span>last page: {r.last_page}</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
